import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Flame, 
  Calendar, 
  Clock, 
  Tag,
  User,
  Users,
  Building,
} from 'lucide-react';
import { HotBillsReport as HotBillsReportType, ReportFilters, HotBill } from '../../types/reports';
import { Bill } from '../../types/bill';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import { hasUpcomingCalendarEvent } from '../../utils/calendarUtils';
import Chip from '../Chip';
import { getPositionColorClasses } from '../../utils/positionColors';
import { getBillLinkClasses } from '../../utils/linkStyles';
import ReportNotesSection from './ReportNotesSection';
import BillTable from '../shared/BillTable';
import SponsorDisplay from '../SponsorDisplay';
import TruncatedText from '../TruncatedText';
import ReportLogoHeader from './ReportLogoHeader';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import CalendarEventDisplay from '../shared/CalendarEventDisplay';
import LoadingList from '@shared/ui/LoadingList';
import ExportMenu from '../ExportMenu';
import ViewToggle from '../ViewToggle';

interface HotBillsFilters extends ReportFilters {
  includePastEvents?: boolean;
}

interface HotBillsReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  filters?: HotBillsFilters;
  /** Pre-loaded hot bills rows (skips internal fetch — used by public report page). */
  prefetchedBills?: any[];
  /** Custom bill URL builder for public-facing links (e.g. /public/bills/{billNumber}). */
  getBillUrl?: (billNumber: string) => string;
  /** Called when the user switches between card and table view. */
  onViewModeChange?: (mode: 'card' | 'table') => void;
  /** Override export handler (used by public report page to route through public download endpoint). */
  onExport?: (format: 'excel' | 'csv' | 'pdf' | 'word', viewMode: 'card' | 'table') => void;
}

// Stable empty filters object to prevent unnecessary re-renders
const EMPTY_FILTERS: HotBillsFilters = {};


// Helper function to transform HotBill to Bill format for BillTable
const transformHotBillToBill = (hotBill: HotBill): Bill => {
  const calendarEvents = hotBill.calendar_events || [];
  // Get the first upcoming event as nextCalendarEvent (for BillTable display)
  const firstEvent = calendarEvents.length > 0 ? calendarEvents[0] : null;
  
  return {
    id: hotBill.bill_id,
    billNumber: hotBill.bill_number,
    title: hotBill.title,
    concerning: hotBill.concerning,
    session: hotBill.session,
    subject: hotBill.subject,
    summary: hotBill.summary,
    status: hotBill.latest_action || 'No recent action',
    lastActivity: hotBill.latest_date || '',
    location: hotBill.latest_location || 'Unknown',
    introducedDate: hotBill.introduced_date || null,
    lastAmendment: hotBill.latest_amendment_date ? { date: hotBill.latest_amendment_date, count: hotBill.amendment_count || 0 } : null,
    amendments: hotBill.amendments || [],
    sponsors: hotBill.sponsors || [],
    committees: [
      ...(hotBill.house_committee ? [{ name: hotBill.house_committee, chamber: 'House' }] : []),
      ...(hotBill.senate_committee ? [{ name: hotBill.senate_committee, chamber: 'Senate' }] : [])
    ],
    categories: Array.isArray(hotBill.subject) ? hotBill.subject : (hotBill.subject ? [hotBill.subject] : []),
    billPosition: hotBill.bill_position,
    isHot: true,
    calendarEvents: calendarEvents,
    // nextCalendarEvent for BillTable calendar date column
    nextCalendarEvent: firstEvent ? {
      calendar_id: parseInt(firstEvent.calendar_id) || 0,
      reading_date: firstEvent.reading_date,
      reading_time: firstEvent.reading_time || undefined,
      reading_title: firstEvent.reading_title,
      room_number: firstEvent.room_number || undefined,
      chamber: firstEvent.chamber,
      upon_adjournment: firstEvent.upon_adjournment
    } : null,
    trackedAt: hotBill.tracked_at,
    trackedBy: hotBill.tracked_by,
    notes: (hotBill.notes as any) || []
  };
};

const HotBillsReport: React.FC<HotBillsReportProps> = ({
  workspaceId,
  workspaceName = 'Workspace',
  organizationId,
  organizationName,
  filters,
  prefetchedBills,
  getBillUrl,
  onViewModeChange,
  onExport,
}) => {
  const isOrgMode = !!organizationId;
  const displayName = isOrgMode ? (organizationName || 'Organization') : workspaceName;
  // Use stable empty object if no filters provided
  const stableFilters = filters || EMPTY_FILTERS;
  const [reportData, setReportData] = useState<HotBillsReportType | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('card');
  const [includePastEvents, setIncludePastEvents] = useState(false);
  const [sortField, setSortField] = useState<'bill_number' | 'title' | 'position' | 'subject' | 'intro_date' | 'calendar_date' | 'sponsors' | 'house_committee' | 'senate_committee'>('bill_number');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Helper to normalize subject to array
  const normalizeSubject = (subject: string | string[] | undefined): string[] => {
    if (!subject) return [];
    return Array.isArray(subject) ? subject : [subject];
  };

  // Org mode: builder already returns one bill per unique bill with workspaces[] populated.
  // No client-side deduplication needed.

  // Sort data
  const sortedBills = React.useMemo(() => {
    if (!reportData?.hotBills || reportData.hotBills.length === 0) return [];
    
    return [...reportData.hotBills].sort((a, b) => {
      let aVal: any;
      let bVal: any;
      
      switch (sortField) {
        case 'bill_number':
          aVal = a.bill_number;
          bVal = b.bill_number;
          break;
        case 'title':
          aVal = a.title;
          bVal = b.title;
          break;
        case 'position':
          // In org mode positions are in workspaces[]; fall back to flat bill_position in workspace mode
          aVal = (a as any).workspaces?.[0]?.position ?? a.bill_position ?? '';
          bVal = (b as any).workspaces?.[0]?.position ?? b.bill_position ?? '';
          break;
        case 'subject':
          aVal = Array.isArray(a.subject) ? a.subject.join(', ') : a.subject;
          bVal = Array.isArray(b.subject) ? b.subject.join(', ') : b.subject;
          break;
        case 'intro_date':
          aVal = a.introduced_date ? new Date(a.introduced_date) : new Date(0);
          bVal = b.introduced_date ? new Date(b.introduced_date) : new Date(0);
          break;
        case 'calendar_date': {
          // Sort by calendar date: records with events come first, then sorted by date/time
          const aEvents = a.calendar_events || [];
          const bEvents = b.calendar_events || [];
          const aHasEvent = aEvents.length > 0;
          const bHasEvent = bEvents.length > 0;
          
          // First: records with events come before records without
          if (aHasEvent && !bHasEvent) return -1;
          if (!aHasEvent && bHasEvent) return 1;
          
          // If both have no events, they're equal for calendar sorting
          if (!aHasEvent && !bHasEvent) return 0;
          
          // Both have events - sort by the first event's date/time
          const aDate = aEvents[0]?.reading_date || '';
          const bDate = bEvents[0]?.reading_date || '';
          const aTime = aEvents[0]?.reading_time || '00:00:00';
          const bTime = bEvents[0]?.reading_time || '00:00:00';
          
          const aDateTime = new Date(`${aDate}T${aTime}`);
          const bDateTime = new Date(`${bDate}T${bTime}`);
          
          if (aDateTime < bDateTime) return sortDirection === 'asc' ? -1 : 1;
          if (aDateTime > bDateTime) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        case 'sponsors':
          aVal = a.sponsors ? a.sponsors.map(s => s.name).join(', ') : '';
          bVal = b.sponsors ? b.sponsors.map(s => s.name).join(', ') : '';
          break;
        case 'house_committee':
          aVal = a.house_committee || '';
          bVal = b.house_committee || '';
          break;
        case 'senate_committee':
          aVal = a.senate_committee || '';
          bVal = b.senate_committee || '';
          break;
        default:
          aVal = a.bill_number;
          bVal = b.bill_number;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reportData?.hotBills, sortField, sortDirection]);


  const { 
    isLoading, 
    error, 
    fetchHotBillsReport,
    exportReport
  } = useWorkspaceReports(workspaceId, organizationId);

  // When prefetchedBills is provided, skip internal fetch and use those rows directly.
  useEffect(() => {
    if (prefetchedBills !== undefined) {
      setReportData({ hotBills: prefetchedBills as HotBill[], summary: { total_hot_bills: prefetchedBills.length, users_tracking: 0, upcoming_events: 0, past_events: 0, committee_events: 0, joint_meetings: 0 } });
      return;
    }
    const fetchData = async () => {
      try {
        const data = await fetchHotBillsReport({
          ...stableFilters,
          includePastEvents
        });
        setReportData(data);
      } catch (err) {
        console.error('Error fetching hot bills data:', err);
      }
    };

    fetchData();
  }, [prefetchedBills, stableFilters, includePastEvents, fetchHotBillsReport]);

  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'word') => {
    try {
      if (onExport) {
        // Public page: delegate to parent which routes through the public download endpoint.
        await onExport(format, viewMode);
      } else {
        // Authenticated page: use the standard workspace/org export endpoint.
        await exportReport('hot-bills', format as any, { 
          ...stableFilters, 
          viewMode,
          includeFullDetails: viewMode === 'card',
          includePastEvents
        } as any);
      }
    } catch (err: any) {
      console.error('Export failed:', err);
    }
  };

  const formatEventDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Map BillTable field names to HotBillsReport field names
  const mapBillTableFieldToSortField = (field: string): typeof sortField => {
    const fieldMap: Record<string, typeof sortField> = {
      'number': 'bill_number',
      'billNumber': 'bill_number',
      'title': 'title',
      'billPosition': 'position',
      'introducedDate': 'intro_date',
      'calendarDate': 'calendar_date',
      'sponsors': 'sponsors',
      'houseCommittees': 'house_committee',
      'senateCommittees': 'senate_committee',
    };
    return fieldMap[field] || 'bill_number';
  };

  // Reverse map: convert internal sort field to BillTable field name
  const mapSortFieldToBillTableField = (field: typeof sortField): string => {
    const reverseMap: Record<typeof sortField, string> = {
      'bill_number': 'number',
      'title': 'title',
      'position': 'billPosition',
      'subject': 'subject',
      'intro_date': 'introducedDate',
      'calendar_date': 'calendarDate',
      'sponsors': 'sponsors',
      'house_committee': 'houseCommittees',
      'senate_committee': 'senateCommittees',
    };
    return reverseMap[field] || 'number';
  };

  const renderTableView = () => {
    if (!reportData) return null;

    // Transform bills for BillTable (maps hot bill fields to Bill shape).
    // In org mode bills already have workspaces[] from the builder; pass it through.
    const transformedBills: Bill[] = sortedBills.map(bill => ({
      ...transformHotBillToBill(bill),
      ...((bill as any).workspaces ? { workspaces: (bill as any).workspaces } : {}),
    }));

    return (
      <BillTable
        bills={transformedBills}
        context={isOrgMode ? 'org-hot-bills-report' : 'hot-bills-report'}
        workspaceId={isOrgMode ? undefined : workspaceId}
        reportMode={true}
        notesDisplayMode="inline"
        sortBy={mapSortFieldToBillTableField(sortField)}
        sortDirection={sortDirection}
        onSort={(field, direction) => {
          setSortField(mapBillTableFieldToSortField(field));
          setSortDirection(direction);
        }}
        getBillHref={getBillUrl ? (bill) => getBillUrl(bill.billNumber || '') : undefined}
        onBillNavigate={getBillUrl ? () => {} : undefined}
        isPublic={!!getBillUrl}
      />
    );
  };

  const renderCardView = () => {
    if (!reportData) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedBills.map((bill, index) => {
          const orgBill = bill as typeof bill & { workspaces?: Array<{ name: string; id: string; position: string; notes: any[] }> };
          return (
            <div key={bill.bill_id ?? bill.bill_number ?? `bill-${index}`} className="card p-4 hover:shadow-md transition-shadow">
              {/* Bill Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Flame className="w-4 h-4 text-error-500" />
                  {getBillUrl ? (
                    <a href={getBillUrl(bill.bill_number || '')} target="_blank" rel="noopener noreferrer">
                      <h3 className={`text-lg ${getBillLinkClasses()}`}>{bill.bill_number}</h3>
                    </a>
                  ) : (
                    <Link to={`/legislation/${bill.bill_id}`}>
                      <h3 className={`text-lg ${getBillLinkClasses()}`}>{bill.bill_number}</h3>
                    </Link>
                  )}
                </div>
                {/* Workspace mode: single position chip stays in header */}
                {!isOrgMode && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(bill.bill_position)}`}>
                    {bill.bill_position}
                  </span>
                )}
              </div>
              {/* Org mode: workspace badges as own row below header */}
              {isOrgMode && orgBill.workspaces && orgBill.workspaces.length > 0 && (
                <WorkspaceBadgeList workspaces={orgBill.workspaces} className="mb-3" />
              )}
              
              <div className="mb-3">
                <TruncatedText 
                  text={bill.title} 
                  lines={2}
                  className="font-medium"
                />
              </div>

              {/* Description */}
              {bill.concerning && (
                <div className="mb-3">
                  <div className="flex items-start space-x-1">
                    <Tag className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" />
                    <TruncatedText 
                      text={bill.concerning} 
                      lines={2}
                      className="text-sm text-text-secondary dark:text-text-dark-secondary"
                    />
                  </div>
                </div>
              )}

              {/* Summary */}
              {bill.summary && (
                <div className="mb-3">
                  <TruncatedText 
                    text={bill.summary} 
                    lines={3}
                    className="text-sm text-text-secondary dark:text-text-dark-secondary"
                  />
                </div>
              )}

              {/* Details Grid */}
              <div className="space-y-2 text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
                {/* Intro Date */}
                {bill.introduced_date && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>Introduced: {formatEventDate(bill.introduced_date)}</span>
                  </div>
                )}

                {/* Subject */}
                {normalizeSubject(bill.subject).length > 0 && (
                  <div className="flex items-start space-x-1">
                    <Tag className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {normalizeSubject(bill.subject).slice(0, 2).map((subj: string, index: number) => (
                        <Chip
                          key={index}
                          label={subj}
                          variant="custom"
                          className="text-xs bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300"
                        />
                      ))}
                      {normalizeSubject(bill.subject).length > 2 && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          +{normalizeSubject(bill.subject).length - 2} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Sponsors */}
                {bill.sponsors && bill.sponsors.length > 0 && (
                  <div className="flex items-start space-x-1">
                    <User className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="text-xs">
                      <span className="font-medium text-text-secondary dark:text-text-dark-secondary">Sponsors: </span>
                      <SponsorDisplay sponsors={bill.sponsors} billNumber={bill.bill_number} isPublic={!!getBillUrl} />
                  </div>
                </div>
                )}

                {/* Committees */}
                {(bill.house_committee || bill.senate_committee) && (
                  <div className="flex items-start space-x-1">
                    <Building className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-500" />
                    <div className="text-xs">
                      <span className="font-medium text-text-secondary dark:text-text-dark-secondary">Committees: </span>
                      <span className="text-text-secondary dark:text-text-dark-secondary">
                        {[bill.house_committee, bill.senate_committee].filter(Boolean).join(', ')}
                      </span>
            </div>
          </div>
                )}

                {/* Tracked By */}
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4" />
                  <span>{bill.tracked_by}</span>
                </div>

                {/* Events */}
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{bill.event_count} events</span>
                </div>

                {/* Latest Action */}
                {bill.latest_action && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-success-500" />
                    <span className="text-xs">{bill.latest_action}</span>
                  </div>
                )}
              </div>

              {hasUpcomingCalendarEvent(bill.calendar_events) && (
                <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <div className="text-xs font-medium text-primary-900 dark:text-primary-300 mb-1">Calendar Date</div>
                  <CalendarEventDisplay
                    events={bill.calendar_events}
                    billNumber={bill.bill_number}
                    billTitle={bill.title}
                    variant="inline"
                    maxDisplay={1}
                    context={getBillUrl ? 'public' : 'authenticated'}
                  />
                </div>
              )}

              {/* Notes — merge notes across workspaces in org mode */}
              {(() => {
                const notesToShow = isOrgMode && orgBill.workspaces
                  ? orgBill.workspaces.flatMap(ws => ws.notes || [])
                  : (Array.isArray(bill.notes) ? bill.notes : []);
                return notesToShow.length > 0 ? (
                  <ReportNotesSection
                    notes={notesToShow}
                    entityName={bill.bill_number}
                    defaultExpanded={false}
                    className="mt-3"
                  />
                ) : null;
              })()}
            </div>
          );
        })}
      </div>
    );
  };

  if (error) {
    return (
      <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4">
        <p className="text-error-700 dark:text-error-400">{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <LoadingList rows={5} label="Loading hot bills" />
    );
  }

  if (!reportData) {
    return (
      <div className="text-center py-8">
        <p className="text-text-secondary dark:text-text-dark-secondary">No hot bills data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Report Logo Header */}
      <ReportLogoHeader workspaceId={workspaceId} />
      
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <Flame className="h-5 w-5 text-error-600" />
          {displayName} - Hot Bills Report
        </h3>
        <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
          Priority bills with upcoming calendar events
        </p>
      </div>

      {/* Compact Summary and Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Compact Summary */}
        <div className="flex items-center space-x-6 text-sm text-text-secondary dark:text-text-dark-secondary">
          <span><strong className="text-error-600">{reportData.summary.total_hot_bills || 0}</strong> Hot Bills</span>
          <span><strong className="text-primary-600">{reportData.summary.upcoming_events || 0}</strong> Upcoming Events</span>
          <span><strong className="text-success-600">{reportData.summary.committee_events || 0}</strong> Committee Events</span>
      </div>

      {/* Controls */}
        <div className="flex items-center space-x-4">
          {/* View Toggle */}
          <ViewToggle
            value={viewMode}
            onChange={(mode) => {
              setViewMode(mode);
              onViewModeChange?.(mode);
            }}
          />

          {/* Export Dropdown — hidden when data is prefetched (public page owns exports) */}
          <ExportMenu
            formats={
              viewMode === 'table'
                ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }]
                : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]
            }
            onExport={(format) => handleExport(format)}
          />

          {/* Past Events Toggle */}
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="includePastEvents"
            checked={includePastEvents}
            onChange={(e) => setIncludePastEvents(e.target.checked)}
            className="rounded border-border-light text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="includePastEvents" className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Include past events
          </label>
          </div>
        </div>
      </div>

      {/* Content */}
      {reportData.hotBills.length === 0 ? (
        <div className="text-center py-12">
          <Flame className="w-16 h-16 text-text-tertiary mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
            No Hot Bills Found
          </h3>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Mark some bills as &quot;hot&quot; in your bill tracking to see them here.
          </p>
        </div>
      ) : (
        <div>
          {viewMode === 'table' ? (
            renderTableView()
          ) : (
            renderCardView()
          )}
        </div>
      )}
    </div>
  );
};

export default HotBillsReport;
